import React, { useState } from "react";
import {
  HeartIcon,
  EyeIcon,
  ChatBubbleBottomCenterIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { formatNumber } from "../../../helpers/NumberFormats";
import { useNavigate } from "react-router-dom";

interface ListsTabProps {
  lists: Array<{
    id: string;
    title: string;
    creator: string;
    creatorId: string;
    rank: number;
    reactions: number;
    comments: number;
  }>;
}

const ListsTab: React.FC<ListsTabProps> = ({ lists }) => {
  const navigate = useNavigate();
  const [sortBy] = useState<"rank" | "reactions" | "newest">("rank");

  const sortedLists = [...lists].sort((a, b) => {
    switch (sortBy) {
      case "rank":
        return a.rank - b.rank; // Lower rank number = higher position
      case "reactions":
        return b.reactions - a.reactions; // Higher reactions first
      case "newest":
        return 0; // Would need createdAt field for actual sorting
      default:
        return a.rank - b.rank;
    }
  });

  const handleListClick = (listId: string) => {
    navigate(`/l/${listId}`);
  };

  const handleCreatorClick = (_creatorId: string, creatorName: string) => {
    navigate(`/u/${creatorName}`);
  };

  if (lists.length === 0) {
    return (
      <div className="text-center py-16">
        <TrophyIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-xl font-medium text-gray-400 mb-2">No Lists Yet</h3>
        <p className="text-gray-500">
          This rapper hasn't been featured in any community lists yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Sort Controls */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">
          Lists Featuring This Rapper ({lists.length})
        </h2>
        {/*  <div className="flex gap-2">
          <button
            onClick={() => setSortBy("rank")}
            className={`btn-sm  ${
              sortBy === "rank"
                ? "bg-blue-600 text-white"
                : "bg-gray-700! text-gray-300 hover:bg-gray-600"
            }`}
          >
            Highest Rank
          </button>
          <button
            onClick={() => setSortBy("reactions")}
            className={`btn-sm  ${
              sortBy === "reactions"
                ? "bg-blue-600 text-white"
                : "bg-gray-700! text-gray-300 hover:bg-gray-600"
            }`}
          >
            Most Reacted
          </button>
        </div> */}
      </div>

      {/* Lists Grid */}
      <div className="grid gap-4">
        {sortedLists.map((list) => (
          <div
            key={list.id}
            onClick={() => handleListClick(list.id)}
            className="bg-gray-800/50 border border-gray-700 rounded-lg p-6 hover:bg-gray-800 transition-colors cursor-pointer group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                  {list.title}
                </h3>
                <p className="text-gray-400 mb-4">
                  Created by{" "}
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCreatorClick(list.creatorId, list.creator);
                    }}
                    className="text-white font-medium cursor-pointer hover:text-blue-400 transition-colors"
                  >
                    {list.creator}
                  </span>
                </p>

                {/* Stats Row */}
                <div className="flex items-center gap-6 text-sm text-gray-400">
                  <div className="flex items-center gap-2">
                    <HeartIcon className="w-4 h-4" />
                    <span>{formatNumber(list.reactions)} reactions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ChatBubbleBottomCenterIcon className="w-4 h-4" />
                    <span>{formatNumber(list.comments)} comments</span>
                  </div>
                </div>
              </div>

              {/* Rank Badge */}
              <div className="flex-shrink-0 ml-4">
                <div className="bg-yellow-500/20 border border-yellow-500 rounded-lg p-4 text-center shadow-md shadow-black/50">
                  <div className="text-yellow-500 font-bold text-2xl">
                    #{list.rank}
                  </div>
                  <div className="text-yellow-400 text-xs font-medium uppercase tracking-wide">
                    Rank
                  </div>
                </div>
              </div>
            </div>

            {/* View List Button */}
            <div className="mt-4 pt-4 border-t border-gray-700">
              <button className="text-blue-400 hover:text-blue-300 text-sm font-medium flex items-center gap-2 group-hover:underline">
                <EyeIcon className="w-4 h-4" />
                View Full List
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Load More */}
      {lists.length > 10 && (
        <div className="text-center mt-8">
          <button className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-md font-medium transition-colors">
            Load More Lists
          </button>
        </div>
      )}
    </div>
  );
};

export default ListsTab;
